import { createWritableStore } from '$lib/stores/CreateLocalStorageStore';
import {
	CesiumTerrainProvider,
	Credit,
	EllipsoidTerrainProvider,
	GeocodeType,
	ImageryLayer,
	Ion,
	IonGeocodeProviderType,
	IonGeocoderService,
	NearFarScalar,
	OpenStreetMapImageryProvider,
	SceneMode,
	TerrainProvider,
	Viewer
} from 'cesium';
import { derived, writable } from 'svelte/store';
import { saveOriginalCesiumInputActions } from './cesium-original-input-actions';
import { confirmModal } from '$lib/components/organisms/confirmModal/confirmModal';
import { toast } from '$lib/components/organisms/toast/toast';

let viewer: Viewer;
let ionGeocoderService: IonGeocoderService;

Ion.defaultAccessToken =
	'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI0MTJiZGY3NC0zYTg5LTRjZDYtYTQxZC04YTI1NTZkNGU2NTgiLCJpZCI6NDUxNDIsImlhdCI6MTYxNDc2MTUwOX0.gqeI3sKNvNOgRHA6N5od1rhShJ2owBOYK83sH8IQ4_U';

/**
 * Attribution is required by the ODbL. Cesium's built-in default credit for this
 * provider is stale — it still names MapQuest and CC-BY-SA — so it is overridden.
 * `showOnScreen` keeps it on the canvas rather than behind the credits lightbox.
 */
const openStreetMapCredit = new Credit(
	'© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>-bidragsytere',
	true
);

/** Base map tiles from OpenStreetMap's volunteer-run servers. */
const createOpenStreetMapBaseLayer = () =>
	new ImageryLayer(
		new OpenStreetMapImageryProvider({
			url: 'https://tile.openstreetmap.org/',
			maximumLevel: 19,
			credit: openStreetMapCredit
		}),
		{}
	);

const createCesiumViewer = async (
	cesiumContainer: HTMLElement,
	creditContainer: string | Element | undefined
): Promise<Viewer> => {
	let terrainProvider: TerrainProvider;
	try {
		terrainProvider = await CesiumTerrainProvider.fromUrl(
			'https://waapi.webatlas.no/wms-hoyde/tms/cesium_qmesh/?api_key=06be3cd6-2849-49ef-8baf-429acc040709',
			{
				requestVertexNormals: true,
				credit:
					'Norkart AS, NASA (SRTM), Statens Kartverk (Nasjonal Detaljert Høydemodell) og kommunene'
				//credit: 'Statens Kartverk (Nasjonal Detaljert Høydemodell) og kommunene'
			}
		);
	} catch (e) {
		console.warn('Custom terrain provider unreachable, falling back to ellipsoid terrain', e);
        toast({
            title: 'Mangler terrengmodell',
            description: 'Vi har for øyeblikket problemer med å nå tjenesten for terrengmodell. Faller tilbake på verdensmodell. Vennligst prøv å laste inn siden på nytt.',
            type: 'error'
        })
		terrainProvider = new EllipsoidTerrainProvider();
	}

	try {
		viewer = new Viewer(cesiumContainer, {
			sceneMode: SceneMode.SCENE3D,
			infoBox: false,
			scene3DOnly: true,
			geocoder: false,
			homeButton: false,
			navigationHelpButton: false,
			//requestRenderMode: true, Explicitly request rendering. Pros: 1. Optimization, 2. Gives us more control of render state. Cons: Implementation time
			sceneModePicker: false, //2D-2.5D-3D toggle | Should not be visible
			timeline: false,
			animation: false,
			baseLayerPicker: false, // map layer picker
			baseLayer: createOpenStreetMapBaseLayer(),
			terrainProvider,
			creditContainer
		});

		// Keep Cesium's original input actions so that we can revert back to them later on when needed, e.g. after modifying their behaviour then wanting to revert to their original behaviour afterwards.
		// Examples: Draw polygons, place new poles, hang new lines, etc. - actions that require the mouse and/or keyboard to not act with out-of-the-box Cesium behaviour.
		saveOriginalCesiumInputActions(viewer.screenSpaceEventHandler);

		/*WebGL 2 may unexpectedly (in the users eyes) lose its context and "crash" Cesium. The event can be catched and handled manually. Example cases:
		 * - Another page does something that takes the GPU too long and the browser or the OS decides to reset the GPU to get control back
		 * - 2 or more pages use too many resources and the browser decides to tell all the pages they lost the context and then restore it only to the front page for now
		 * - The user switches graphics cards (Turns on/off one or more in the control panel) or updates their driver
		 *
		 * To simulate loss of context:
		 * const gl = canvas.getContext("webgl2");
		 * gl.getExtension("WEBGL_lose_context").loseContext();
		 */

		viewer.canvas.addEventListener(
			'webglcontextlost',
			function (event) {
				event.preventDefault();

                confirmModal(
                    `<p style="margin-bottom: 11px;">Det ser det ut til at nettleseren din har hatt et problem med å kjøre WebGL. Last inn siden på nytt for å løse problemet.</p> <span style="font-weight:500;">Grunner til at dette kan ha skjedd:</span>
					<ul style="list-style-type:disc; margin-left: 18px;">
						<li>En annen fane i nettleseren eller på PC-en bruker mye ressurser.</li>
						<li>PC-en er satt i hvilemodus eller stått stille i lengre tid.</li>
						<li>Grafikkortet er oppdatert eller har endret instillinger.</li>
					</ul>`,
					() => {
						//onOk
						window.location.reload();
					},
					() => {},
					{
						labels: {
							title: 'Siden må lastes inn på nytt',
							confirm: 'Last inn på nytt'
						}
					})
			},
			false
		);
		
		viewer.scene.globe.translucency.frontFaceAlphaByDistance = new NearFarScalar(
			4.0,
			0.0,
			100.0,
			1.0
		);
		viewer.scene.globe.translucency.enabled = false;

		/**
		 * The lost context can be more gracefully restored, but there is currently a bug when destroying a viewer regarding its context:
		 * https://github.com/CesiumGS/cesium/issues/11533
		 *
		 * Pick this up at a later point.
		 * viewer.canvas.addEventListener("webglcontextrestored", createCesiumViewer(), false);
		 */
	} catch (e) {
		console.error('Could not create Cesium Viewer', e);
	}

	return viewer;
	//viewer.extend(viewerCesiumInspectorMixin);
};

const useCesiumViewer = () => viewer;

const useCesiumGeocoderService = async (query: string) => {
	if (!ionGeocoderService) {
		ionGeocoderService = new IonGeocoderService({ 
			scene: viewer.scene, 
			geocodeProviderType: IonGeocodeProviderType.GOOGLE 
		});
	}
	const result = await ionGeocoderService.geocode(query, GeocodeType.AUTOCOMPLETE);
	return result;
};

const busyStateMessage = writable<string>('');
const showBusyState = derived(
	busyStateMessage,
	($busyStateMessage) => $busyStateMessage.length > 0
);

function createBusyIndicator() {
	const defaultIndicator = '';

	const { subscribe, set, update } = writable<string>(defaultIndicator);
	return {
		update,
		set,
		subscribe,
		loadingData: () =>
			update((store) => {
				store = `
				<span class="flex items-center">
					<span class="loading loading-xs mr-3 h-4 loading-spinner text-white" />
				</span>
				<span class="text-white">Laster område...</span>
				`;
				return store;
			}),
		idle: () =>
			update((store) => {
				store = defaultIndicator;
				return store;
			})
	};
}
const busyIndicator = createBusyIndicator();
const showBusyIndicator = derived(busyIndicator, ($busyIndicator) => $busyIndicator.length > 0);

const customTerrainOffsets = createWritableStore<{
	offsetInMeters: number,
	color: [number, number, number, number],
	active: boolean
}[]>('terrainOffsets', []);

// Configure both stores to store their values in localStorage
customTerrainOffsets.useLocalStorage();

const createMapSettingsState = () => {
	const defaultSettings = {
		enablePartnerColors: false,
		showPartnerEntities: true,
		partnerCompanies: undefined as Map<number, string> | undefined
	}

	const {subscribe, set, update} = writable(defaultSettings);
	return {
		update,
		set,
		subscribe
	};
}
const createCameraState = () => {
	const STATES = {
		RAY: 'RAY',
		CAMERA: 'CAMERA'
	}

	const defaultLabel: {
		loading: boolean;
		cameraState: keyof typeof STATES
	} = {
		loading: false,
		cameraState: 'RAY'
	};

	const {subscribe, set, update} = writable(defaultLabel);

	return {
		update,
		set,
		subscribe
	};
}

const showThreeScene = writable<boolean>(false);
const temporaryThreeScene = writable<boolean>(false);

/**
 * May be implemented:
 * Keeps track of states regarding fetching of entities in map. As fetching may become a resource-heavy
 * operation, the states should provide help to offload unecessary usage of resources and provide a better user experience.
 
const createEntityFetchingState = () => {
	const STATES = {
		IDLE: 'IDLE', // Idle, stale
		LOADING: 'LOADING', // Fetching, loading
		FULFILLED: 'FULFILLED' // Success, error
	} as const;

	const initialValues: {
		existingArea: any;
		loadingArea: any;
		fetchState: keyof typeof STATES;
		queries: {
			controller: undefined | AbortController;
			sceneCenter: {
				latitude: number,
				longitude: number
			}
		}[]
	} = {
		existingArea: null,
		loadingArea: null,
		fetchState: 'LOADING',
		controller: undefined
	};

	const {subscribe, set, update} = writable(initialValues);

	return {
		update,
		set,
		subscribe
	};
}
const entityFetchingState = createEntityFetchingState();
*/
const cameraState = createCameraState();
const mapSettingsState = createMapSettingsState();

export {
	createCesiumViewer,
	useCesiumViewer,
	useCesiumGeocoderService,
	busyStateMessage,
	busyIndicator,
	showBusyState,
	showBusyIndicator,
	cameraState,
	mapSettingsState,
	showThreeScene,
	temporaryThreeScene,
	customTerrainOffsets
	//entityFetchingState
};
